import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import SiteCoreLinks from "@/components/SiteCoreLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import HubCountryLinks from "@/components/HubCountryLinks";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
// /marriage — hub for marriage registration with foreign nationals
import { createFileRoute, Link } from "@tanstack/react-router";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { VoiceSearchFAQ } from "@/components/seo/VoiceSearchFAQ";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { MARRIAGE_NATIONALITIES, MARRIAGE_REGIONS } from "@/data/marriage-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { Heart, ArrowRight, ShieldCheck } from "lucide-react";
import { TrustLayer } from "@/components/TrustLayer";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/marriage/")({
  loader: async () => ({
    clusterFaq: await buildClusterFaqJsonLdAsync("marriage", "th", "https://nyclanguageinstitute.com/marriage"),
    voiceFaqs: (await import("@/data/voice-faqs")).MARRIAGE_VOICE_FAQS,
  }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/marriage`;
    const title = `จดทะเบียนสมรสกับชาวต่างชาติ ${MARRIAGE_NATIONALITIES.length}+ สัญชาติ · ครบจบใน 5-10 วัน | NYC`;
    const desc = `บริการจดทะเบียนสมรสกับชาวต่างชาติ ${MARRIAGE_NATIONALITIES.length}+ สัญชาติ — พาไปสถานทูต + Affidavit Single Status + แปลไทย + MFA + นัดอำเภอ เสร็จ 5-10 วันทำการ — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    return {
      meta: [
        { title: ctaTitle(title, "/marriage", "th") }, { name: "description", content: ctaDesc(desc, "/marriage", "th")},
        { property: "og:title", content: ctaTitle(title, "/marriage", "th")}, { property: "og:description", content: ctaDesc(desc, "/marriage", "th")},
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/marriage")],
      scripts: [speakableSchema(), 
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/marriage", serviceType: "International Marriage Registration", name: title, description: desc, priceFromThb: 8500 }),
          howToSchema({
            name: "วิธีจดทะเบียนสมรสกับชาวต่างชาติในประเทศไทย",
            description: "ขั้นตอนครบจดทะเบียนสมรสกับคู่สมรสชาวต่างชาติที่อำเภอไทย — ขอ Affidavit Single Status ที่สถานทูต · แปลไทย · MFA รับรอง · นัดอำเภอ",
            totalTime: "PT10D",
            estimatedCost: { currency: "THB", value: "8500" },
            steps: [
              { name: "เตรียมพาสปอร์ต + นัดสถานทูตคู่สมรส", text: "คู่สมรสชาวต่างชาตินัดสถานทูตของตน เพื่อขอ Affidavit of Single Status (รับรองว่าโสด ไม่มีคู่สมรส)", url: `${url}#nationalities` },
              { name: "พาไปสถานทูต + ล่ามรับรอง (ถ้าจำเป็น)", text: "ทีม NYC พาไปสถานทูตพร้อมล่ามภาษา (สำหรับสัญชาติที่ต้องสัมภาษณ์) — รับ Affidavit ออกมาในวันเดียว" },
              { name: "แปลเป็นภาษาไทย + รับรองคำแปล", text: "แปล Affidavit เป็นภาษาไทยโดยนักแปลรับรอง + Notary รับรองความถูกต้อง", url: `${SITE}/translate` },
              { name: "รับรองที่กรมการกงสุล (MFA)", text: "ส่งคำแปลเข้ารับรองที่กรมการกงสุล ถนนแจ้งวัฒนะ — ใช้เวลา 2-3 วันทำการ", url: `${SITE}/apostille` },
              { name: "นัดอำเภอจดทะเบียนสมรส", text: "ทีมนัดวันที่อำเภอ (เลือกเขตในกรุงเทพฯ ได้) พร้อมพยาน 2 คน — รับ คร.2 + คร.3 ในวันเดียว" },
              { name: "แปลทะเบียนสมรส + Apostille (ออปชัน)", text: "หากต้องการใช้ทะเบียนสมรสไทยที่ต่างประเทศ NYC แปลและ Apostille / Legalization ต่อให้ได้เลย" },
            ],
          }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "@id": `${url}#page`,
              name: title, description: desc,
              hasPart: MARRIAGE_NATIONALITIES.map((n) => ({
                "@type": "WebPage",
                name: `จดทะเบียนสมรสกับ${n.nameTh}`,
                url: `${SITE}/marriage/${n.slug}`,
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "จดทะเบียนสมรส", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing international marriage registration tooling." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: MarriageHub,
});

function MarriageHub() {
  const { voiceFaqs } = Route.useLoaderData();
  const url = `${SITE}/marriage`;
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <AIAnswerBox
          question={`จดทะเบียนสมรสกับชาวต่างชาติในไทย ต้องทำอะไรบ้าง ราคาเท่าไร?`}
          answer={`NYC พาคู่สมรสไทย–ต่างชาติจดทะเบียนสมรสครบ ${MARRIAGE_NATIONALITIES.length}+ สัญชาติ — พาเข้าสถานทูตทำ Affidavit Single Status, แปลไทยโดยนักแปล MoJ, รับรอง MFA แจ้งวัฒนะ และนัดอำเภอ เริ่ม ฿8,500 เสร็จ 5–10 วันทำการ`}
          stats={[
            { label: "ราคาเริ่ม", value: "฿8,500/คู่" },
            { label: "ระยะเวลา", value: "5–10 วัน" },
            { label: "สัญชาติ", value: `${MARRIAGE_NATIONALITIES.length}+` },
            { label: "พื้นที่", value: "ทุกอำเภอทั่วไทย" },
          ]}
          sourceUrl={url}
          lang="th"
        />
      </div>
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Heart className="h-3.5 w-3.5" /> Marriage Registration · {MARRIAGE_NATIONALITIES.length}+ Nationalities
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            จดทะเบียนสมรสกับชาวต่างชาติ {MARRIAGE_NATIONALITIES.length}+ สัญชาติ
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            ครบทุกขั้นตอน: พาไปสถานทูต → Affidavit Single Status → แปลไทย + MFA → นัดอำเภอ — เริ่ม ฿8,500 เสร็จ 5-10 วันทำการ
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Marriage Registration Thailand">
              NYC Language Institute พาคู่สมรสไทย–ต่างชาติจดทะเบียนสมรสครบทุกขั้นตอน {MARRIAGE_NATIONALITIES.length}+ สัญชาติ — พาเข้าสถานทูตทำหนังสือรับรองสถานภาพโสด (Affidavit of Single Status), แปลภาษาไทยโดยนักแปล MoJ, รับรองที่กรมการกงสุล (MFA แจ้งวัฒนะ) แล้วนัดที่ว่าการอำเภอ ค่าบริการเริ่ม ฿8,500 เสร็จ 5–10 วันทำการ
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "สัญชาติที่รองรับ", v: `${MARRIAGE_NATIONALITIES.length}+ สัญชาติ` },
              { k: "ราคาเริ่มต้น", v: "฿8,500 / คู่" },
              { k: "ระยะเวลา", v: "5–10 วันทำการ" },
              { k: "ขั้นตอน", v: "สถานทูต → MoJ → MFA → อำเภอ" },
              { k: "เอกสารหลัก", v: "Affidavit Single Status + Passport" },
              { k: "พื้นที่จดทะเบียน", v: "ทุกอำเภอทั่วไทย" },
            ]} />
          </div>
        </div>
      </section>

      <QuotableStatsStrip cluster="marriage" locale="th" variant="dark" />

      <AnswerBoxTop cluster="marriage" locale="th" />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8 sm:mt-10">
        <VoiceSearchFAQ topic="Marriage Registration Thailand" faqs={voiceFaqs} entity="NYC Marriage Registration Thailand" />
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        {MARRIAGE_REGIONS.map((region) => {
          const list = MARRIAGE_NATIONALITIES.filter((n) => n.region === region.id);
          if (!list.length) return null;
          return (
            <section key={region.id} className="mb-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep">{region.label}</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((n) => (
                  <Link
                    key={n.slug}
                    to="/marriage/$nationality"
                    params={{ nationality: n.slug }}
                    className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                  >
                    <div className="text-3xl leading-none">{n.flag}</div>
                    <div className="flex-1">
                      <div className="font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">
                        {n.nameTh}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">{n.countryEn}</div>
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-600">
                        <ShieldCheck className="h-3 w-3 text-emerald-600" />
                        เริ่ม ฿{n.serviceFeeTh.toLocaleString()} · {n.turnaround}
                      </div>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <TrustLayer variant="compact" />
      <HubCountryLinks service="marriage-abroad" lang="th" />
          <ServicePageEnhancer serviceName="จดทะเบียนสมรส" locale="th" />
      <SiteCoreLinks />
    
      <PlanningToolsLinks locale="th" />
      </main>
  );
}
